import { Col, Row } from "react-grid-system";
import {
  convertDateStringToMonthDayYearFormat,
  INPUT_MASKS,
  maskInput,
} from "../../../../lib/formatting";
import StyledInput from "../../../common/inputs/StyledInput";
import StyledCheckbox from "../../../common/inputs/StyledCheckbox";
import { CMInfoFormInput, CMInfoSubtool } from "../../CMInfo.hooks";
import Collapsible from "../../../common/Collapsible";
import LabeledCheckbox from "../LabeledCheckbox";
import styles from "./CMInfoSchoolSubtool.module.css";
import { useState } from "react";
import {
  CMInfoUpdateSectionType,
  ValidationErrors,
} from "../../../../lib/api/models";
import CMInfoSubtoolButtons from "../CMInfoSubtoolButtons";
import ValidationMessage from "../../../common/ValidationMessage/ValidationMessage";
import { partialSuccessErrorMessage } from "../../constants";

const strings = {
  school: "School",
  graduationDate: "Graduation Date",
  graduationDatePlaceholder: "MM/YY",
  lastVerifiedDate: "Last Verified Date",
  nullDate: "XX/XX/XXXX",
  graduated: "Graduated",
  yes: "Yes",
  na: "N/A",
  clear: "Clear",
  reset: "Reset",
  update: "Update",
};

type CMInfoSchoolSubtoolProps = {
  className?: string;
  onShowConfirmationModal: (
    variant: "reset" | "clear",
    onSuccess: () => void,
  ) => void;
  formState: CMInfoFormInput[CMInfoSubtool.School];
  initialFormState: CMInfoFormInput[CMInfoSubtool.School];
  generalDispatch: (
    payload: Partial<CMInfoFormInput[CMInfoSubtool.School]>,
  ) => void;
  validationMessages: ValidationErrors | undefined;
  onUpdate: () => void;
  currentlyUpdating: CMInfoSubtool | "ALL" | null;
  failedSections: CMInfoUpdateSectionType[];
  startExpanded: boolean;
};

export default function CMInfoSchoolSubtool({
  className,
  onShowConfirmationModal,
  formState,
  initialFormState,
  generalDispatch,
  validationMessages,
  onUpdate,
  currentlyUpdating,
  failedSections,
  startExpanded = false,
}: CMInfoSchoolSubtoolProps) {
  const [isExpanded, setIsExpanded] = useState(
    formState.schoolDueForVerification || startExpanded,
  );
  const [hasUnverifiedChanges, setHasUnverifiedChanges] = useState(false);

  return (
    <Collapsible
      disabled={
        formState.schoolDueForVerification || formState.isSchoolVerified
      }
      className={className}
      headerText={strings.school}
      isExpanded={isExpanded}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <Row>
        <Col md={5}>
          <StyledInput
            innerContentClassName={styles.label}
            label={strings.school}
            value={formState.school}
            onChange={(e) => {
              generalDispatch({
                school: e.currentTarget.value,
                graduationDate:
                  e.currentTarget.value === "" ? "" : formState.graduationDate,
                isSchoolVerified:
                  // Should be automatically verified if:
                  //   Already verified
                  //   OR Previously had a value not ""
                  //   OR New value is not ""
                  formState.isSchoolVerified ||
                  formState.school.trim() !== "" ||
                  e.currentTarget.value.trim() !== "",
              });
              setHasUnverifiedChanges(true);
            }}
            onBlur={() => {
              const school = formState.school.trim();
              generalDispatch({
                school: school,
                graduationDate: school === "" ? "" : formState.graduationDate,
                isSchoolVerified: formState.isSchoolVerified || school !== "",
              });
            }}
            maxLength={26}
            disabled={formState.graduationStatus !== null}
            validationMessages={validationMessages?.school}
          />
        </Col>
        <Col md={2}>
          <StyledInput
            innerContentClassName={styles.label}
            label={strings.graduationDate}
            placeholder={strings.graduationDatePlaceholder}
            value={formState.graduationDate}
            onChange={(e) => {
              generalDispatch({
                graduationDate: maskInput(
                  INPUT_MASKS.date,
                  e.currentTarget.value,
                ),
                isSchoolVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
            disabled={
              formState.school === "" || formState.graduationStatus !== null
            }
            validationMessages={validationMessages?.graduationDate}
          />
        </Col>
        <Col md={3}>
          <LabeledCheckbox
            label={strings.lastVerifiedDate}
            checkboxLabel={convertDateStringToMonthDayYearFormat(
              formState.schoolLastVerifiedDate,
              strings.nullDate,
            )}
            checked={formState.isSchoolVerified}
            onCheck={(isSchoolVerified) =>
              generalDispatch({ isSchoolVerified })
            }
            isExpired={formState.schoolDueForVerification}
            disabled={hasUnverifiedChanges}
          />
        </Col>
      </Row>
      <Row className={styles.graduatedLabel}>
        <Col>{strings.graduated}</Col>
      </Row>
      <Row>
        <Col md={1}>
          <StyledCheckbox
            label={strings.yes}
            checked={formState.graduationStatus === "grad"}
            onChange={(e) => {
              generalDispatch({
                graduationStatus: e.currentTarget.checked ? "grad" : null,
                isSchoolVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
          />
        </Col>
        <Col md={1}>
          <StyledCheckbox
            label={strings.na}
            checked={formState.graduationStatus === "NA"}
            onChange={(e) => {
              generalDispatch({
                graduationStatus: e.currentTarget.checked ? "NA" : null,
                graduationDate: "",
                school: "",
                isSchoolVerified: true,
              });
              setHasUnverifiedChanges(true);
            }}
          />
        </Col>
        <Col>
          <CMInfoSubtoolButtons
            hideScriptButton
            onReset={() =>
              onShowConfirmationModal("reset", () => {
                generalDispatch(initialFormState);
                setHasUnverifiedChanges(false);
              })
            }
            isResetDisabled={!formState.isSchoolVerified}
            onClear={() =>
              onShowConfirmationModal("clear", () => {
                generalDispatch({
                  school: "",
                  graduationDate: "",
                  graduationStatus: null,
                  isSchoolVerified: true,
                });
                setHasUnverifiedChanges(true);
              })
            }
            isClearDisabled={
              formState.school === "" &&
              formState.graduationDate === "" &&
              formState.graduationStatus === null &&
              (!formState.isSchoolVerified || hasUnverifiedChanges)
            }
            onUpdate={onUpdate}
            isUpdateDisabled={
              !formState.isSchoolVerified || !!currentlyUpdating
            }
            isUpdateLoading={currentlyUpdating === CMInfoSubtool.School}
          />
        </Col>
      </Row>
      {failedSections.includes("school") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[partialSuccessErrorMessage(strings.school)]}
            />
          </Col>
        </Row>
      ) : null}
      {failedSections.includes("graduationDate") ? (
        <Row>
          <Col>
            <ValidationMessage
              validationMessages={[
                partialSuccessErrorMessage(strings.graduationDate),
              ]}
            />
          </Col>
        </Row>
      ) : null}
    </Collapsible>
  );
}
